import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@lib/supabase';
import {
  getBadgeIcon,
  groupBadgesByCategory,
  type BadgeWithCategory,
} from '@lib/badges';
import Modal from '@components/Modal';
import Button from '@components/Button';

const STREAK_365_GRADIENT_COLORS = [
  '#3BBAC3',
  '#4FBFD0',
  '#598ED1',
  '#6A7EE0',
  '#7660DE',
  '#8A5AE6',
];
const STREAK_365_GRADIENT = `linear-gradient(135deg, ${STREAK_365_GRADIENT_COLORS.join(', ')})`;

const isSpecialGradientBadge = (badge?: BadgeWithCategory | null) =>
  Boolean(
    badge &&
      ((badge.requirement_type === 'streak_days' &&
        badge.requirement_value === 365) ||
        (badge.requirement_type === 'total_minutes' &&
          badge.requirement_value === 6000))
  );

const buildBadgeAssetUrl = (value?: string | null) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (trimmed.startsWith('http')) {
    return trimmed;
  }
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').replace(
    /\/$/,
    ''
  );
  const normalizedPath = trimmed.replace(/^\/+/, '');
  return `${supabaseUrl}/storage/v1/object/public/${encodeURI(normalizedPath)}`;
};

function BadgesPublic() {
  const [badges, setBadges] = useState<BadgeWithCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadBadges() {
      setLoading(true);
      setError(null);

      try {
        const { data, error: supabaseError } = await supabase
          .from('badges')
          .select(
            `
            *,
            category:badge_categories(*)
          `
          )
          .order('created_at', { ascending: true });

        if (supabaseError) {
          throw supabaseError;
        }

        if (!cancelled) {
          setBadges(((data as BadgeWithCategory[]) ?? []).filter(Boolean));
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error
              ? err.message
              : 'Unable to load badges right now.';
          setError(message || 'Unable to load badges right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadBadges();

    return () => {
      cancelled = true;
    };
  }, []);

  const groupedBadges = useMemo(() => groupBadgesByCategory(badges), [badges]);

  const orderedBadgeGroups = useMemo(
    () =>
      Array.from(groupedBadges.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
          ([categoryName, badgesInCategory]) =>
            [
              categoryName,
              [...badgesInCategory].sort(
                (a, b) =>
                  (a.requirement_value ?? 0) - (b.requirement_value ?? 0)
              ),
            ] as [string, BadgeWithCategory[]]
        ),
    [groupedBadges]
  );

  const hasBadges = badges.length > 0;

  const [selectedBadge, setSelectedBadge] = useState<BadgeWithCategory | null>(
    null
  );

  const selectedBadgeGradientBackground = isSpecialGradientBadge(selectedBadge);
  const selectedBadgeHasCustomBackground = Boolean(
    selectedBadge && (selectedBadge.color || selectedBadgeGradientBackground)
  );
  const selectedBadgeModalStyle = (() => {
    if (selectedBadgeGradientBackground) {
      return {
        borderLeftColor: STREAK_365_GRADIENT_COLORS[0],
        borderColor: STREAK_365_GRADIENT_COLORS[0],
        backgroundImage: STREAK_365_GRADIENT,
        backgroundColor: 'transparent',
      };
    }
    if (selectedBadge?.color) {
      return { backgroundColor: selectedBadge.color };
    }
    return undefined;
  })();

  // Helpers to build modal messages for streak/time badges.
  const parseNumberFromBadgeName = (name?: string | null): number | null => {
    if (!name) return null;
    const m = name.match(/(\d+)/);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  const _TO_19 = [
    'zero',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
  ];

  const _TENS = [
    '',
    '',
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
  ];

  const numberToWords = (n: number): string => {
    if (!Number.isFinite(n) || n < 0) return String(n);
    if (n < 20) return _TO_19[n] || String(n);
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const rest = n % 10;
      const tensWord = _TENS[ten] ?? String(ten);
      const restWord = _TO_19[rest] ?? String(rest);
      return rest === 0 ? tensWord : `${tensWord}-${restWord}`;
    }
    if (n < 1000) {
      const h = Math.floor(n / 100);
      const rest = n % 100;
      return rest === 0
        ? `${_TO_19[h]} hundred`
        : `${_TO_19[h]} hundred ${numberToWords(rest)}`;
    }
    return String(n);
  };

  const formatLockedModalMessage = (name?: string | null): string => {
    const badge = name ?? '';
    const num = parseNumberFromBadgeName(badge);

    // Streak badges: show written-word count and days wording
    if (num !== null && /\bstreak|day(s)?\b/i.test(badge)) {
      const word = numberToWords(num);
      return `Complete guided breathing sessions ${word} ${
        num === 1 ? 'day' : 'days'
      } in a row to earn this badge. Once you unlock it, this feature becomes available to you.`;
    }

    // Time badges: minutes/hours — produce minute-based sentence
    if (
      num !== null &&
      /\b(minute|minutes|min|hour|hours|hr|hrs)\b/i.test(badge)
    ) {
      const isHour = /\bhour(s)?\b|\bhr(s)?\b/i.test(badge);
      const minutes = isHour ? num * 60 : num;
      return `Complete at least ${minutes} minutes of guided breathing in a single day to earn this badge. Once you unlock it, this feature becomes available to you.`;
    }

    // fallback: keep the badge's description if present, otherwise a default
    return '';
  };

  return (
    <section className="py-16">
      <div className="mx-auto max-w-5xl space-y-10">
        <div className="text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Explore All Badges
          </h1>

          <p className="mt-4 text-base text-gray-600">
            Browse the full badge catalog to see every milestone you can unlock
            with <span className="logo-font font-bold">Xhalin</span>.
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
            Loading badges...
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-700">
            {error}
          </div>
        ) : hasBadges ? (
          <div className="space-y-8">
            {orderedBadgeGroups.map(([categoryName, badgesInCategory]) => {
              const normalizedCategory = categoryName.trim().toLowerCase();
              const isHighlightedGroup = ['consistency', 'time'].includes(
                normalizedCategory
              );

              return (
                <div
                  key={categoryName}
                  className={
                    isHighlightedGroup
                      ? 'rounded-3xl bg-white/95 p-6 shadow-[0_15px_40px_rgba(15,23,42,0.12)] ring-1 ring-gray-100'
                      : undefined
                  }
                >
                  <h2 className="gradient-text mb-4 pb-6 text-center text-4xl font-bold">
                    {categoryName}
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {badgesInCategory.map((badge) => {
                      const hasGradientBackground =
                        isSpecialGradientBadge(badge);
                      const hasVibrantBackground = Boolean(
                        badge.color || hasGradientBackground
                      );

                      const badgeStyle = (() => {
                        if (hasGradientBackground) {
                          return {
                            borderLeftColor: STREAK_365_GRADIENT_COLORS[0],
                            borderColor: STREAK_365_GRADIENT_COLORS[0],
                            backgroundImage: STREAK_365_GRADIENT,
                            backgroundColor: 'transparent',
                          };
                        }

                        if (badge.color) {
                          return {
                            borderLeftColor: badge.color,
                            borderColor: badge.color,
                            backgroundColor: badge.color,
                          };
                        }

                        return undefined;
                      })();

                      const textColor = hasVibrantBackground
                        ? 'text-white'
                        : 'text-gray-900';
                      const descriptionColor = hasVibrantBackground
                        ? 'text-white'
                        : 'text-gray-600';

                      return (
                        <article
                          key={badge.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedBadge(badge)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedBadge(badge);
                            }
                          }}
                          className="flex items-start gap-3 rounded-xl border border-l-4 bg-white p-4 text-left shadow-lg ring-1 ring-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-300"
                          style={badgeStyle}
                        >
                          {badge.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(badge.image_url) || undefined
                              }
                              alt={badge.name}
                              className="h-20 w-20 rounded-md object-cover"
                            />
                          ) : (
                            <span className="text-5xl" aria-hidden>
                              {getBadgeIcon(badge)}
                            </span>
                          )}
                          <div className="flex-1">
                            <p className={`font-semibold ${textColor}`}>
                              {badge.name}
                            </p>
                            {badge.description && (
                              <p className={`mt-1 text-sm ${descriptionColor}`}>
                                {badge.description}
                              </p>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Modal for selected badge */}
            {selectedBadge && (
              <Modal
                title={selectedBadge.name}
                onClose={() => setSelectedBadge(null)}
                className="max-w-xl"
                style={selectedBadgeModalStyle}
                textColorClass={
                  selectedBadgeHasCustomBackground
                    ? 'text-white'
                    : 'text-gray-900'
                }
                closeButtonClass={
                  selectedBadgeHasCustomBackground
                    ? 'text-white transition hover:bg-white/10 hover:text-white'
                    : undefined
                }
              >
                <div className="flex justify-center">
                  <div className="w-full rounded-lg bg-transparent p-4 sm:p-6">
                    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-10">
                      <div
                        className="h-36 w-36 flex-shrink-0 overflow-hidden rounded-full border border-gray-100 shadow-inner sm:h-56 sm:w-56"
                        style={{
                          backgroundColor: selectedBadge.color ?? undefined,
                        }}
                      >
                        {selectedBadge.image_url ? (
                          <img
                            src={
                              buildBadgeAssetUrl(selectedBadge.image_url) ||
                              undefined
                            }
                            alt={selectedBadge.name || 'badge'}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-3xl">
                            {selectedBadge.name?.slice(0, 1) ?? '🏅'}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 text-center sm:text-left">
                        {selectedBadge.name && (
                          <div className="text-xl font-semibold text-gray-900 sm:text-2xl">
                            {selectedBadge.name}
                          </div>
                        )}
                        <div className="mt-4 text-sm text-gray-700">
                          <p>
                            {formatLockedModalMessage(selectedBadge.name) ||
                              selectedBadge.description}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Button
                        type="button"
                        className="item-selected w-full"
                        onClick={() => setSelectedBadge(null)}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </Modal>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
            No badges have been added yet. Check back soon!
          </div>
        )}
      </div>
    </section>
  );
}

export default BadgesPublic;
